import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Store, Wallet, CreditCard, Coins, Smartphone, ArrowRightLeft, Shield, Zap, MapPin, Loader2, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { isSupported as webauthnSupported, loginPasskey } from '@/lib/webauthn-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

// The Mojo Connect PWA (wallet + chat, the iOS route) lives on its own origin.
// Absolute on purpose: a relative "/connect/" only ever resolved on the UAT
// nginx alias — on Firebase Hosting it falls into the SPA rewrite and just
// reloads this app.
const CONNECT_APP_URL = process.env.REACT_APP_CONNECT_APP_URL || 'https://mojo-connect-pwa.web.app/';

const SA_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape',
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [userType, setUserType] = useState('merchant');
  const [loading, setLoading] = useState(false);

  // Password visibility state
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  // Signup OTP state
  const [signupOtpStep, setSignupOtpStep] = useState(false); // true = show OTP input before submitting
  const [signupOtp, setSignupOtp] = useState('');
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [signupMaskedPhone, setSignupMaskedPhone] = useState('');
  const [signupOtpLoading, setSignupOtpLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email' | 'otp' | 'done'
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetMaskedPhone, setResetMaskedPhone] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    id_number: '',
    business_name: '',
    province: '',
    business_address: ''
  });

  const [detectedProvince, setDetectedProvince] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (!window.google?.maps?.Geocoder) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status !== 'OK' || !results?.length) return;
          for (const component of results[0].address_components) {
            if (component.types.includes('administrative_area_level_1')) {
              const name = component.long_name;
              if (SA_PROVINCES.includes(name)) setDetectedProvince(name);
              break;
            }
          }
        });
      },
      () => {} // silently ignore denied/unavailable
    );
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendSignupOtp = async () => {
    if (!formData.phone_number) {
      toast.error('Please enter your phone number first');
      return;
    }
    setSignupOtpLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-signup-otp`, { phone_number: formData.phone_number });
      setSignupMaskedPhone(res.data.masked_phone || formData.phone_number.slice(-4));
      setSignupOtpSent(true);
      setSignupOtpStep(true);
      toast.success(res.data.test_otp
        ? `Test OTP: ${res.data.test_otp}`
        : `OTP sent to ${res.data.masked_phone}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSignupOtpLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    // For registration, require OTP verification first
    if (authMode === 'register' && !signupOtpSent) {
      toast.error('Please verify your phone number with an OTP first');
      return;
    }
    if (authMode === 'register' && signupOtpStep) {
      // Verify OTP before proceeding
      if (!signupOtp) {
        toast.error('Please enter the OTP sent to your phone');
        return;
      }
      setSignupOtpLoading(true);
      try {
        await axios.post(`${API}/auth/verify-signup-otp`, {
          phone_number: formData.phone_number,
          otp: signupOtp
        });
        setSignupOtpStep(false);
        toast.success('Phone verified!');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Invalid OTP');
        setSignupOtpLoading(false);
        return;
      } finally {
        setSignupOtpLoading(false);
      }
    }

    setLoading(true);

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login'
        // The login field accepts an email OR a phone number; the backend
        // resolves either (phone is disambiguated by password). Sent as
        // `identifier` — the backend still accepts the legacy `email` key too.
        ? { identifier: formData.email, password: formData.password }
        : { ...formData, user_type: userType };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (authMode === 'login') {
        // Phase 0 — token now lives in an HttpOnly cookie set by the server.
        // We no longer persist `mojo_token` in localStorage. Profile info
        // stays (it's not a credential and the UI uses it for rendering).
        localStorage.setItem('mojo_user', JSON.stringify(response.data.user));
        localStorage.setItem('mojo_wallet_id', response.data.wallet_id);

        toast.success('Login successful!');
        const redirectPath = response.data.user.user_type === 'merchant' ? '/merchant' : '/customer';
        setTimeout(() => navigate(redirectPath), 500);
      } else {
        // Registration - redirect directly to dashboard
        // Phase 0 — token in HttpOnly cookie; only profile/wallet kept locally.
        localStorage.setItem('mojo_user', JSON.stringify(response.data.user));
        localStorage.setItem('mojo_wallet_id', response.data.wallet_id);
        
        toast.success('Account created successfully!');
        toast.info('Complete ID verification to unlock all features');
        
        // Redirect directly to dashboard
        const redirectPath = userType === 'merchant' ? '/merchant' : '/customer';
        setTimeout(() => navigate(redirectPath), 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Phase 2 — passkey login.
   *
   * Tries the WebAuthn ceremony first. If the user has no passkey for the
   * email (404) or the browser doesn't support it, surface a clear error
   * and let them fall back to email+password via the standard form.
   */
  const handlePasskeyLogin = async () => {
    if (!webauthnSupported()) {
      toast.error('Passkeys are not supported in this browser');
      return;
    }
    if (!formData.email) {
      toast.error('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      const beginR = await axios.post(`${API}/webauthn/auth/begin`, { email: formData.email });
      const publicKey = JSON.parse(beginR.data.publicKey);
      const credential = await loginPasskey(publicKey);
      const finishR = await axios.post(`${API}/webauthn/auth/finish`, { credential });
      localStorage.setItem('mojo_user', JSON.stringify(finishR.data.user));
      toast.success('Logged in with passkey');
      const redirectPath = finishR.data.user.user_type === 'merchant' ? '/merchant' : '/customer';
      setTimeout(() => navigate(redirectPath), 300);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('No passkey on file for that account — sign in with password instead');
      } else if (error?.name === 'NotAllowedError') {
        toast.error('Passkey prompt cancelled');
      } else {
        toast.error(error?.response?.data?.detail || error?.message || 'Passkey login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = async () => {
    setLoading(true);
    try {
      // The proxy captures verification results directly into the DB,
      // so we check the wallet endpoint for the latest status
      const walletRes = await axios.get(
        `${API}/wallet/balance`,
        { headers: { Authorization: `Bearer ${tempUserData.token}` } }
      );
      const vs = walletRes.data?.verification_status;

      if (vs?.verified) {
        localStorage.setItem('mojo_user', JSON.stringify({ ...tempUserData.user, biometric_verified: true }));
        localStorage.setItem('mojo_wallet_id', tempUserData.wallet_id);
        toast.success('Verification successful!');
        const redirectPath = tempUserData.user.user_type === 'merchant' ? '/merchant' : '/customer';
        setTimeout(() => navigate(redirectPath), 1000);
      } else if (vs?.score !== null && vs?.score !== undefined) {
        toast.error(`Verification score (${vs.score}%) is below the required 80% threshold. Please try again.`);
      } else {
        toast.warning('No results found yet — please complete the face capture and ID scan first, then click this button again.');
      }
    } catch (error) {
      toast.error('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
      setShowVerification(false);
    }
  };

  const handleForgotPasswordOpen = () => {
    setResetStep('email');
    setResetEmail('');
    setResetOtp('');
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetMaskedPhone('');
    setShowForgotPassword(true);
  };

  const handleRequestResetOtp = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-password-reset`, { email: resetEmail });
      setResetMaskedPhone(response.data.masked_phone || '');
      toast.success('OTP sent to your registered phone number');
      setResetStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        otp: resetOtp,
        new_password: resetNewPassword,
      });
      toast.success('Password reset successful! Please log in with your new password.');
      setShowForgotPassword(false);
      setResetStep('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  const features = [
    {
      icon: <Store className="w-8 h-8" />,
      title: 'Merchant Wallet',
      description: 'Accept payments, manage transactions, and grow your business'
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: 'Wallet to Wallet',
      description: 'Instant transfers between Mojo Money wallets'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Card Payments',
      description: 'Accept card payments with low transaction fees'
    },
    {
      icon: <Coins className="w-8 h-8" />,
      title: 'ACE Token',
      description: 'Trade carbon-backed crypto tokens on Hedera DLT'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'Airtime & Data',
      description: 'Purchase airtime and data bundles instantly'
    },
    {
      icon: <ArrowRightLeft className="w-8 h-8" />,
      title: 'Money Transfer',
      description: 'Send money across Africa with ACE stable coin'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Biometric Security',
      description: 'Advanced biometric authentication via VirtuallVerify'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Instant Settlement',
      description: 'Real-time transaction processing and settlement'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]">
      {/* Header */}
      <header className="border-b border-[#F2B10C]/20 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/sticker.svg" alt="Mojo Money" className="h-12 w-12" />
            <div>
              <h1 className="text-2xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Payments & Remittances</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              asChild
              variant="outline"
              data-testid="view-on-ios-btn"
              className="border-[#F2B10C] text-[#1B0C34] hover:bg-[#F2B10C]/10 font-semibold px-4 sm:px-5 rounded-full"
            >
              <a href={CONNECT_APP_URL} aria-label="View Mojo Connect on iOS">
                <Smartphone className="w-4 h-4" />
                <span className="hidden sm:inline">View on iOS</span>
                <span className="sm:hidden">iOS</span>
              </a>
            </Button>
            <Button
              data-testid="get-started-btn"
              onClick={() => setShowAuth(true)}
              className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold px-6 rounded-full"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center max-w-4xl mx-auto fade-in">
          <h2 className="text-5xl md:text-6xl font-bold text-[#1B0C34] mb-6">
            Africa's Future of
            <span className="block text-[#F2B10C] mt-2">Payments & Remittances</span>
          </h2>
          <p className="text-xl text-[#1B0C34]/70 mb-8 max-w-2xl mx-auto">
            Powered by Hedera DLT and ACE token stable coin. 
            Accept payments, send money, and trade with confidence.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              data-testid="merchant-signup-btn"
              onClick={() => {
                setUserType('merchant');
                setAuthMode('register');
                setShowAuth(true);
              }}
              size="lg"
              className="bg-[#1B0C34] hover:bg-[#2D1854] text-white px-8 rounded-full text-lg"
            >
              Merchant Sign Up
            </Button>
            <Button
              data-testid="customer-signup-btn"
              onClick={() => {
                setUserType('customer');
                setAuthMode('register');
                setShowAuth(true);
              }}
              size="lg"
              variant="outline"
              className="border-[#F2B10C] text-[#1B0C34] hover:bg-[#F2B10C]/10 px-8 rounded-full text-lg"
            >
              Customer Sign Up
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20">
        <h3 className="text-4xl font-bold text-center text-[#1B0C34] mb-12">Everything You Need</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="card-hover bg-white border-[#F2B10C]/20" data-testid={`feature-card-${index}`}>
              <CardHeader>
                <div className="w-16 h-16 rounded-full bg-[#F2B10C]/10 flex items-center justify-center text-[#F2B10C] mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-[#1B0C34]">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-[#1B0C34]/70">{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col" data-testid="auth-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl text-[#1B0C34]">Welcome to Mojo Money</DialogTitle>
            <DialogDescription>Sign in or create an account to get started</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            <Tabs value={authMode} onValueChange={setAuthMode}>
              <TabsList className="grid w-full grid-cols-2 sticky top-0 z-10 bg-white">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <form onSubmit={handleAuth} className="space-y-4 py-4">
                <div>
                  <Label htmlFor="email">Email or phone number</Label>
                  <Input
                    id="email"
                    name="email"
                    /* Not type="email": a phone number is valid here too, and the
                       browser's email-format validation would reject it. */
                    type="text"
                    inputMode="text"
                    autoComplete="username"
                    placeholder="you@example.com or 0821234567"
                    data-testid="login-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showLoginPassword ? 'text' : 'password'}
                      data-testid="login-password-input"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1B0C34]/50 hover:text-[#1B0C34]"
                      tabIndex={-1}
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>

                {/* Phase 2 — passkey login. Only renders if the browser
                    supports WebAuthn at all; backend then decides whether
                    THIS user has a registered passkey. */}
                {webauthnSupported() && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handlePasskeyLogin}
                    disabled={loading || !formData.email}
                    data-testid="login-passkey-btn"
                  >
                    <Fingerprint className="w-4 h-4 mr-2" />
                    Sign in with passkey
                  </Button>
                )}

                <div className="text-center space-y-1">
                  <button
                    type="button"
                    onClick={handleForgotPasswordOpen}
                    className="text-sm text-[#145539] hover:underline block w-full"
                  >
                    Forgot Password?
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAuth(false); navigate('/recovery'); }}
                    className="text-sm text-[#145539] hover:underline block w-full"
                    data-testid="lost-device-link"
                  >
                    Lost the device with your passkey?
                  </button>
                </div>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleAuth} className="space-y-3 py-4">
                <div>
                  <Label className="text-sm">Account Type</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      data-testid="select-merchant-btn"
                      variant={userType === 'merchant' ? 'default' : 'outline'}
                      className={userType === 'merchant' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                      onClick={() => setUserType('merchant')}
                    >
                      Merchant
                    </Button>
                    <Button
                      type="button"
                      data-testid="select-customer-btn"
                      variant={userType === 'customer' ? 'default' : 'outline'}
                      className={userType === 'customer' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                      onClick={() => setUserType('customer')}
                    >
                      Customer
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="full_name" className="text-sm">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    data-testid="register-fullname-input"
                    className="h-9"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg_email" className="text-sm">Email</Label>
                  <Input
                    id="reg_email"
                    name="email"
                    type="email"
                    data-testid="register-email-input"
                    className="h-9"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone_number" className="text-sm">Phone Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="phone_number"
                      name="phone_number"
                      data-testid="register-phone-input"
                      placeholder="+27..."
                      className="h-9 flex-1"
                      value={formData.phone_number}
                      onChange={(e) => { handleInputChange(e); setSignupOtpSent(false); setSignupOtpStep(false); setSignupOtp(''); }}
                      required
                    />
                    <Button
                      type="button"
                      onClick={handleSendSignupOtp}
                      disabled={signupOtpLoading || signupOtpSent}
                      className="h-9 px-3 text-xs bg-[#145539] hover:bg-[#1e7049] text-white whitespace-nowrap"
                    >
                      {signupOtpLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : signupOtpSent ? '✓ Sent' : 'Send OTP'}
                    </Button>
                  </div>
                  {signupOtpStep && (
                    <div className="mt-2">
                      <Input
                        placeholder={`Enter OTP sent to ${signupMaskedPhone}`}
                        maxLength={6}
                        className="h-9"
                        value={signupOtp}
                        onChange={(e) => setSignupOtp(e.target.value)}
                      />
                      <p className="text-xs text-[#1B0C34]/60 mt-1">Enter the 6-digit code to verify your phone</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="id_number" className="text-sm">ID Number</Label>
                  <Input
                    id="id_number"
                    name="id_number"
                    data-testid="register-id-input"
                    placeholder="13-digit SA ID"
                    className="h-9"
                    value={formData.id_number}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Province — shown for all user types */}
                <div>
                  <Label htmlFor="province" className="text-sm">Province</Label>
                  <select
                    id="province"
                    name="province"
                    data-testid="register-province-select"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select Province</option>
                    {detectedProvince && (
                      <>
                        <option value={detectedProvince}>{detectedProvince} (current location)</option>
                        <option disabled>──────────────</option>
                      </>
                    )}
                    {SA_PROVINCES.filter(p => p !== detectedProvince).map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                {/* Merchant-specific fields */}
                {userType === 'merchant' && (
                  <>
                    <div>
                      <Label htmlFor="business_name" className="text-sm">Business Name</Label>
                      <Input
                        id="business_name"
                        name="business_name"
                        data-testid="register-business-name-input"
                        placeholder="Your business name"
                        className="h-9"
                        value={formData.business_name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="business_address" className="text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Business Address
                      </Label>
                      <Input
                        id="business_address"
                        name="business_address"
                        data-testid="register-address-input"
                        placeholder="Enter your business address"
                        className="h-9"
                        value={formData.business_address}
                        onChange={handleInputChange}
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="reg_password" className="text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="reg_password"
                      name="password"
                      type={showRegisterPassword ? 'text' : 'password'}
                      data-testid="register-password-input"
                      className="h-9"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1B0C34]/50 hover:text-[#1B0C34]"
                      tabIndex={-1}
                    >
                      {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </form>
            </TabsContent>
            </Tabs>
          </div>
          {authMode === 'register' && (
            <div className="flex-shrink-0 pt-2 border-t border-gray-100">
              <Button
                type="button"
                data-testid="register-submit-btn"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] h-10"
                disabled={loading || signupOtpLoading}
                onClick={handleAuth}
              >
                {loading || signupOtpLoading
                  ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" />{signupOtpStep ? 'Verifying...' : 'Creating Account...'}</>
                  : signupOtpStep ? 'Verify OTP & Create Account' : 'Create Account'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Reset Password</DialogTitle>
            <DialogDescription>
              {resetStep === 'email' && 'Enter your email to receive an OTP on your registered phone.'}
              {resetStep === 'otp' && `Enter the OTP sent to ${resetMaskedPhone} and your new password.`}
            </DialogDescription>
          </DialogHeader>

          {resetStep === 'email' && (
            <form onSubmit={handleRequestResetOtp} className="space-y-4">
              <div>
                <Label htmlFor="reset_email">Email Address</Label>
                <Input
                  id="reset_email"
                  type="email"
                  placeholder="your@email.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                disabled={resetLoading}
              >
                {resetLoading ? <><Loader2 size={16} className="animate-spin mr-2" />Sending...</> : 'Send OTP'}
              </Button>
            </form>
          )}

          {resetStep === 'otp' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset_otp">OTP Code</Label>
                <Input
                  id="reset_otp"
                  placeholder="6-digit OTP"
                  maxLength={6}
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reset_new_pass">New Password</Label>
                <div className="relative">
                  <Input
                    id="reset_new_pass"
                    type={showResetNewPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetNewPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1B0C34]/50 hover:text-[#1B0C34]"
                    tabIndex={-1}
                  >
                    {showResetNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="reset_confirm_pass">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="reset_confirm_pass"
                    type={showResetConfirmPassword ? 'text' : 'password'}
                    placeholder="Repeat new password"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1B0C34]/50 hover:text-[#1B0C34]"
                    tabIndex={-1}
                  >
                    {showResetConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                disabled={resetLoading}
              >
                {resetLoading ? <><Loader2 size={16} className="animate-spin mr-2" />Resetting...</> : 'Reset Password'}
              </Button>
              <button
                type="button"
                onClick={() => setResetStep('email')}
                className="w-full text-sm text-[#145539] hover:underline"
              >
                Back
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#1B0C34] text-white py-8 mt-20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm opacity-70">© 2025 Mojo Money. Powered by Hedera DLT & ACE Token.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
